import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useUnifiedCourseLessons } from '@/lib/hooks/use-unified-courses'
import LessonForm from './LessonForm'
import type { Database } from '@/lib/supabase/supabase-types'

type Lesson = Database["public"]["Tables"]["lessons"]["Row"]

interface LessonManagementProps {
  courseId: string
  lessons?: Lesson[]
}

export default function LessonManagement({ courseId, lessons }: LessonManagementProps) {
  const { createLesson, updateLesson, deleteLesson, isCreating, isUpdating, isDeleting } = useUnifiedCourseLessons(courseId)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null)

  const handleCreateLesson = (data: any) => {
    if (createLesson) {
      createLesson(data)
      setShowCreateDialog(false)
    }
  }

  const handleUpdateLesson = (data: any) => {
    if (updateLesson) {
      updateLesson(data)
      setEditingLesson(null)
    }
  }

  const handleDeleteLesson = (lessonId: string) => {
    if (confirm('Are you sure you want to delete this lesson?')) {
      if (deleteLesson) {
        deleteLesson(lessonId)
      }
    }
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A'
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const maxPosition = Math.max(0, ...(lessons?.map(l => l.position) || []))

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold">Lessons</h3>
        <Button onClick={() => setShowCreateDialog(true)}>
          Add Lesson
        </Button>
      </div>

      <div className="grid gap-4">
        {lessons?.map((lesson) => (
          <Card key={lesson.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-sm bg-muted px-2 py-1 rounded">
                      #{lesson.position}
                    </span>
                    {lesson.title}
                    <Badge variant={lesson.lesson_type === 'video' ? 'default' : lesson.lesson_type === 'reading' ? 'secondary' : 'outline'}>
                      {lesson.lesson_type}
                    </Badge>
                    {lesson.is_required && (
                      <Badge variant="outline">Required</Badge>
                    )}
                  </CardTitle>
                  <CardDescription>{lesson.description}</CardDescription>
                  
                  <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                    {lesson.lesson_type === 'video' && (
                      <>
                        {lesson.vimeo_video_id && (
                          <span>Video ID: {lesson.vimeo_video_id}</span>
                        )}
                        <span>Duration: {formatDuration(lesson.duration_seconds)}</span>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingLesson(lesson)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteLesson(lesson.id)}
                    disabled={isDeleting}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}

        {!lessons?.length && (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">No lessons found. Add your first lesson to get started.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Lesson Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Lesson</DialogTitle>
            <DialogDescription>
              Create a new lesson for this course.
            </DialogDescription>
          </DialogHeader>
          <LessonForm
            courseId={courseId}
            maxPosition={maxPosition}
            onSubmit={handleCreateLesson}
            isLoading={isCreating}
            onCancel={() => setShowCreateDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Lesson Dialog */}
      <Dialog open={!!editingLesson} onOpenChange={() => setEditingLesson(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Lesson</DialogTitle>
            <DialogDescription>
              Update lesson information.
            </DialogDescription>
          </DialogHeader>
          <LessonForm
            lesson={editingLesson}
            courseId={courseId}
            maxPosition={maxPosition}
            onSubmit={handleUpdateLesson}
            isLoading={isUpdating}
            onCancel={() => setEditingLesson(null)}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}